<template>
  <div class="rd-screen">
    <report-tools :copy="shownRows.length > 0" @copy="copyTable">
      <label class="ui-btn ui-btn--sm rd-toggle" :class="{ 'is-on': includeBranches }">
        <input v-model="includeBranches" type="checkbox" class="rd-visually-hidden" />
        <v-icon size="16" :color="includeBranches ? '#9a3412' : '#5f5852'">
          {{ includeBranches ? "mdi-checkbox-marked" : "mdi-checkbox-blank-outline" }}
        </v-icon>
        {{ $t("more_restaurants_report.whole_company") }}
      </label>
    </report-tools>

    <report-state
      v-if="!selectedCompany"
      icon="mdi-domain"
      :title="$t('report_mobile.employees_pick_company')"
    ></report-state>
    <report-state v-else-if="loading" kind="loading"></report-state>
    <report-state
      v-else-if="!shownRows.length"
      icon="mdi-account-off-outline"
      :title="emptyTitle"
      :text="emptyText"
    ></report-state>

    <ui-table
      v-else
      variant="report"
      wrap-class="rd-card--scroll"
      table-class="mr-items"
      :columns="columns"
      :rows="shownRows"
      :row-key="(row) => row.rowKey"
      :row-class="itemRowClass"
      :totals="sums"
      :total-label="$t('total')"
    >
      <template #cell-worker="{ row }">
        <span v-if="row.showWorker" class="rd-person">
          <span class="ui-avatar ui-avatar--neutral ui-avatar--sm">{{ row.initials }}</span>
          <span class="rd-person__name">{{ row.worker }}</span>
        </span>
      </template>

      <template #cell-shiftNumber="{ row }">
        <report-shift-tag v-if="row.shiftNumber" :shift="row.shiftNumber"></report-shift-tag>
        <span v-else class="rd-dash" aria-hidden="true">–</span>
      </template>
    </ui-table>
  </div>
</template>

<script>
import ReportMealsTabEmployeeItems from "@/components/report/more-restaurants/tabs/ReportMealsTabEmployeeItems.vue";
import hasShiftBinding from "../../../shared/hasShiftBinding";
import hasShiftSubtitle from "../../../shared/hasShiftSubtitle";
import hasMoreWorkerItemRows from "../../../shared/more/hasMoreWorkerItemRows";
import ReportShiftTag from "../../../shared/ReportShiftTag.vue";
import { copyText, rowsToTsv } from "../../copyTable";
import ReportState from "../../ReportState.vue";
import ReportTools from "../../ReportTools.vue";

/**
 * Radnici – po stavkama (firme "više restorana") na računaru: ko je šta naručio po danu i smeni (paket / obrok /
 * jelo, količina, jela u paketu, preuzeto), ime radnika samo u prvom redu. Firmu, smene i pretragu čita iz
 * zajedničke trake, a "Cela firma" (sa filijalama) je u alatima. Nasleđuje učitavanje, štampu i Excel iz starog taba.
 */
export default {
  name: "ReportDesktopMoreWorkerItems",
  extends: ReportMealsTabEmployeeItems,
  mixins: [hasShiftBinding, hasShiftSubtitle, hasMoreWorkerItemRows],
  components: { ReportShiftTag, ReportState, ReportTools },
  computed: {
    /** Kolone tabele: iste služe za zaglavlje, redove i zbirni red. */
    columns() {
      return [
        { key: "worker", label: this.$t("worker"), name: true },
        { key: "dateText", label: this.$t("date"), cls: "rd-col-time", num: true, muted: true },
        { key: "shiftNumber", label: this.$t("shifts"), align: "center" },
        { key: "itemText", label: this.$t("more_restaurants_report.item"), cellCls: "is-name", title: (row) => row.itemText },
        { key: "quantity", label: this.$t("more_restaurants_report.quantity_short"), align: "end", num: true },
        { key: "comboText", label: this.$t("more_restaurants_report.package_items"), muted: true, cellCls: "mr-items__combo" },
        ...(this.showAmount ? [{ key: "amount", label: this.$t("amount"), align: "end", num: true }] : []),
        { key: "quantity_payed", label: this.$t("taken"), cls: "rd-col-taken", align: "end", num: true, zero: true },
      ];
    },
    emptyText() {
      return this.filters.search ? "" : this.$t("report_desktop.empty_hint");
    },
    kpis() {
      return [
        { key: "quantity", label: this.$t("report_desktop.total_meals"), value: this.sums.quantity, tone: "inverse" },
        { key: "taken", label: this.$t("taken"), value: this.sums.quantity_payed },
        { key: "workers", label: this.$t("report_desktop.workers"), value: this.workerCount },
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.shownRows.length ? kpis : []);
      },
      immediate: true,
    },
    loading: {
      handler(loading) {
        this.$emit("loading", loading);
      },
      immediate: true,
    },
  },
  methods: {
    itemRowClass(row) {
      return { "mr-items__first": row.showWorker };
    },
    async copyTable() {
      const headers = [
        this.$t("worker"),
        this.$t("date"),
        this.$t("shifts"),
        this.$t("more_restaurants_report.item"),
        this.$t("more_restaurants_report.quantity_short"),
        this.$t("more_restaurants_report.package_items"),
        ...(this.showAmount ? [this.$t("amount")] : []),
        this.$t("taken"),
      ];
      const rows = this.shownRows.map((row) => [
        row.worker,
        row.dateText,
        row.shiftNumber || "",
        row.itemText,
        row.quantity,
        row.comboText,
        ...(this.showAmount ? [row.amount] : []),
        row.quantity_payed,
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
